'use client';
import cn from 'classnames';
import { MouseEvent, ReactNode } from 'react';

import './PaginationBar.style.scss';

type PaginationBarProps = {
  className?: string;
  totalPages: number;
  /** 1 index page number */
  currentPage: number;

  onNavigate: (pageNo: number) => void;
};

const paginationEllipsis = (
  <div
    className={cn(
      'PaginationBar-element PaginationBar-value PaginationBar-ellipsis'
    )}
  >
    ...
  </div>
);

const determineFirstFiveEllipsis = (
  currentPage: number,
  totalPages: number
): ReactNode => {
  if (currentPage !== 3 && totalPages !== 7) {
    return paginationEllipsis;
  }

  if (currentPage === 3 && totalPages === 6) {
    return null;
  }

  if (currentPage < totalPages - 2) {
    return paginationEllipsis;
  }

  return null;
};

const determineLastFiveEllipsis = (
  currentPage: number,
  totalPages: number
): ReactNode => {
  if (currentPage === 4 && totalPages === 6) {
    return null;
  }

  if (currentPage <= 4) {
    return paginationEllipsis;
  }

  return paginationEllipsis;
};

function PaginationBar(props: PaginationBarProps) {
  const { className = '', totalPages, currentPage, onNavigate } = props;

  const handleNavigation = (event: MouseEvent<HTMLDivElement>) => {
    const pageData = parseInt(event.currentTarget.dataset.value as string, 10);
    if (pageData >= 0) {
      onNavigate(pageData);
    }
  };

  const getMiddleContent = () => {
    const isInitialPages = currentPage < 4;
    const isEndingPages = totalPages - 4 < currentPage;

    // Case for only showing 5 or less pages
    if (totalPages <= 5) {
      // return;
      const pageArray = Array(totalPages).fill('page');
      return pageArray.map((_, index) => {
        const pageNumber = index + 1;
        return (
          <div
            key={`pagination_${pageNumber}`}
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: currentPage === pageNumber
            })}
            role='button'
            data-value={pageNumber}
            onClick={handleNavigation}
          >
            {pageNumber}
          </div>
        );
      });
    }

    if (isInitialPages) {
      // Return the first 5 page button elements
      let pageArray = Array(totalPages > 5 ? 5 : totalPages).fill('page');

      const firstFive = pageArray.map((_, index) => {
        const pageNumber = index + 1;
        return (
          <div
            key={`pagination_${pageNumber}`}
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: currentPage === pageNumber
            })}
            role='button'
            data-value={pageNumber}
            onClick={handleNavigation}
          >
            {pageNumber}
          </div>
        );
      });
      if (totalPages <= 5) {
        return firstFive;
      }

      return (
        <>
          {firstFive}
          {determineFirstFiveEllipsis(currentPage, totalPages)}
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: currentPage === totalPages
            })}
            role='button'
            data-value={totalPages}
            onClick={handleNavigation}
          >
            {totalPages}
          </div>
        </>
      );
    } else if (isEndingPages) {
      const lastPages = [4, 3, 2, 1, 0]
        .map((pageNumber, index) => {
          if (totalPages === 5 && index === 0) {
            return null;
          }
          return (
            <div
              key={`pagination-l5_${pageNumber}`}
              className={cn(
                'PaginationBar-element PaginationBar-value cursor',
                {
                  active: currentPage === totalPages - pageNumber
                }
              )}
              role='button'
              data-value={totalPages - pageNumber}
              onClick={handleNavigation}
            >
              {totalPages - pageNumber}
            </div>
          );
        })
        .filter(Boolean);

      return (
        <>
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: currentPage === 1
            })}
            role='button'
            data-value={1}
            onClick={handleNavigation}
          >
            {1}
          </div>
          {determineLastFiveEllipsis(currentPage, totalPages)}

          {lastPages}
        </>
      );
    } else {
      // IT IS IN THE MIDDLE
      return (
        <>
          {/* FIRST PAGE */}
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: currentPage === 1
            })}
            role='button'
            data-value={1}
            onClick={handleNavigation}
          >
            {1}
          </div>
          {/* ELLIPSIS */}
          <div
            className={cn(
              'PaginationBar-element PaginationBar-value PaginationBar-ellipsis'
            )}
          >
            ...
          </div>
          {/* CURRENT - 1*/}
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor')}
            role='button'
            data-value={currentPage - 1}
            onClick={handleNavigation}
          >
            {currentPage - 1}
          </div>
          {/* CURRENT*/}
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: true
            })}
            role='button'
          >
            {currentPage}
          </div>
          {/* CURRENT + 1*/}
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor')}
            role='button'
            data-value={currentPage + 1}
            onClick={handleNavigation}
          >
            {currentPage + 1}
          </div>
          <div
            className={cn(
              'PaginationBar-element PaginationBar-value PaginationBar-ellipsis'
            )}
          >
            ...
          </div>
          <div
            className={cn('PaginationBar-element PaginationBar-value cursor', {
              active: currentPage === totalPages
            })}
            role='button'
            data-value={totalPages}
            onClick={handleNavigation}
          >
            {totalPages}
          </div>
        </>
      );
    }
  };

  return (
    <div className={cn('PaginationBar', className)}>
      <div
        className={cn('PaginationBar-element PaginationBar-back-one', {
          cursor: currentPage !== 1,
          'not-allowed': currentPage === 1
        })}
        role='button'
        data-value={currentPage - 1}
        onClick={currentPage === 1 ? undefined : handleNavigation}
      >
        {'<'}
      </div>
      {getMiddleContent()}
      <div
        className={cn('PaginationBar-element PaginationBar-forward-one', {
          cursor: currentPage !== totalPages,
          'not-allowed': currentPage === totalPages
        })}
        role='button'
        data-value={currentPage + 1}
        onClick={currentPage === totalPages ? undefined : handleNavigation}
      >
        {'>'}
      </div>
    </div>
  );
}

export default PaginationBar;
